/**
 * AppShell.tsx — D.4.1 Application Shell Layout
 *
 * Provides the persistent navigation chrome for the PhiloFund application.
 *
 * Structure:
 *   ┌──────────────────────────────────────────────────────┐
 *   │ AppBar: Logo | Welcome Text | Nav Links | User Menu  │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Main Content Area (children / <Outlet />)            │
 *   └──────────────────────────────────────────────────────┘
 *
 * Figma design spec:
 *   - AppBar background matches page warm peach (#FAF5F0) with a subtle bottom border
 *   - LEFT: Logo component
 *   - NEXT TO LOGO: "WELCOME, {NAME}!" in bold uppercase (authenticated only)
 *   - RIGHT (flex-end): Nav links as plain text with colored underline on active route
 *     - Donor active underline: green (#4CAF50)
 *     - Org admin active underline: purple (#9C27B0)
 *   - "My Matches" link (donor) and "My Donors" (org admin) carry a small
 *     count badge; badge color matches the role's active underline color
 *   - Content background: warm peach (#FAF5F0)
 *
 * Nav links per Figma:
 *   - Donor:    Home (-> /dashboard) | My Matches badge (-> /organizations) | Settings (-> /settings)
 *   - Org:      Donations (-> /dashboard) | My Donors badge (-> /donors) | Settings (-> /settings)
 *
 * Responsive strategy:
 *   - Desktop (md+): navigation links shown inline in the AppBar toolbar.
 *   - Mobile (<md): links hidden; a hamburger button opens an MUI Drawer.
 *
 * Auth-conditional rendering:
 *   - Unauthenticated: Sign In + Sign Up links
 *   - Donor: Home, My Matches, Settings
 *   - Org Admin: Donations, My Donors, Settings
 *   - Platform Admin: all donor links + Admin Panel
 *
 * Role is read from `user.appRole` — attached by the auth service after
 * resolving the user's Firestore profile / custom token claims.
 *
 * Accessibility:
 *   - AppBar has role="banner" (from <header> element)
 *   - Nav links have descriptive text
 *   - User menu button has aria-label="Open account menu"
 *   - Hamburger button has aria-label="Open navigation menu"
 *   - Drawer has role="dialog" via MUI Drawer semantics
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/services/auth.service';
import { COLOR } from '@/theme/tokens';
import { Logo } from '@/components/common/Logo';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import { ProfileMenu } from '@/components/common/ProfileMenu';
import type { UserRole } from '@philofund/shared/types/user';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAWER_WIDTH = 260;

/** Warm peach background from Figma for the content area. */
const CONTENT_BG = '#FAF5F0';

// ---------------------------------------------------------------------------
// Nav link definitions per role
// ---------------------------------------------------------------------------

interface NavLink {
  label: string;
  href: string;
  /** If true, render a badge next to this link (donor/org count indicator). */
  badge?: boolean;
}

/** Public links shown to all visitors (authenticated or not). */
const PUBLIC_LINKS: NavLink[] = [
  { label: 'Browse Nonprofits', href: '/organizations' },
];

/**
 * Role-scoped navigation (PRD-021 segregation of duties): each role sees ONLY
 * its own surfaces. No role inherits another role's links — mirroring the
 * route guards in App.tsx and the disjoint DRF permission classes backend-side.
 */
const DONOR_LINKS: NavLink[] = [
  { label: 'Home', href: '/donor' },
  { label: 'Browse Nonprofits', href: '/organizations', badge: true },
  { label: 'My Donations', href: '/donor/donations' },
  { label: 'Recurring gifts', href: '/donor/subscriptions' },
  { label: 'Portfolio', href: '/donor/portfolio' },
  { label: 'Impact', href: '/donor/impact' },
  { label: 'Matches', href: '/donor/matches' },
  { label: 'Receipts', href: '/donor/receipts' },
  { label: 'Statements', href: '/donor/statements' },
  { label: 'Settings', href: '/donor/settings' },
];

const ORG_ADMIN_LINKS: NavLink[] = [
  { label: 'Dashboard', href: '/organization' },
  { label: 'Donations Received', href: '/organization/donations' },
  { label: 'Donors', href: '/organization/donors' },
  { label: 'Recurring Gifts', href: '/organization/subscriptions' },
  { label: 'Refunds', href: '/organization/refunds' },
  { label: 'Payouts & Reports', href: '/organization/payouts' },
  { label: 'Analytics', href: '/organization/analytics' },
  { label: 'Org Profile', href: '/organization/profile' },
  { label: 'Site Content', href: '/organization/content' },
  { label: 'Verification', href: '/organization/verification' },
  { label: 'Documents', href: '/organization/documents' },
  { label: 'Payments', href: '/organization/stripe' },
  { label: 'Team', href: '/organization/team' },
  { label: 'Import', href: '/organization/import' },
  { label: 'Plan', href: '/organization/plan' },
  { label: 'Settings', href: '/organization/settings' },
];

const PLATFORM_ADMIN_LINKS: NavLink[] = [
  { label: 'Dashboard', href: '/admin' },
  { label: 'Organizations', href: '/admin/organizations' },
  { label: 'Verification', href: '/admin/verification' },
  { label: 'Disputes', href: '/admin/disputes' },
  { label: 'Refunds', href: '/admin/refunds' },
  { label: 'Donors', href: '/admin/donors' },
  { label: 'CRM', href: '/admin/crm' },
  { label: 'Marketing', href: '/admin/marketing' },
  { label: 'Accounting', href: '/admin/accounting' },
  { label: 'Compliance', href: '/admin/compliance' },
  { label: 'Users', href: '/admin/users' },
  { label: 'Permissions', href: '/admin/permissions' },
  { label: 'Import', href: '/admin/import' },
  { label: 'Activity', href: '/admin/activity' },
  { label: 'Settings', href: '/admin/settings' },
];

// ---------------------------------------------------------------------------
// Helper: extract role from the user object stored in the auth store.
// ---------------------------------------------------------------------------

function getUserRole(user: unknown): UserRole | undefined {
  if (
    user !== null &&
    typeof user === 'object' &&
    'appRole' in user &&
    typeof (user as Record<string, unknown>)['appRole'] === 'string'
  ) {
    return (user as Record<string, unknown>)['appRole'] as UserRole;
  }
  return undefined;
}

function getNavLinks(role: UserRole | undefined): NavLink[] {
  switch (role) {
    case 'platform_admin':
      return PLATFORM_ADMIN_LINKS;
    case 'org_admin':
      return ORG_ADMIN_LINKS;
    case 'donor':
    default:
      return DONOR_LINKS;
  }
}

function getUserDisplayName(user: unknown): string {
  if (user === null || typeof user !== 'object') return '';
  const u = user as Record<string, unknown>;
  if (typeof u['displayName'] === 'string' && u['displayName'].length > 0) {
    return u['displayName'];
  }
  if (typeof u['email'] === 'string') {
    return u['email'];
  }
  return '';
}

/** Read the user's avatar URL (Firebase User exposes `photoURL`). */
function getUserPhotoUrl(user: unknown): string {
  if (user !== null && typeof user === 'object') {
    const v = (user as Record<string, unknown>)['photoURL'];
    if (typeof v === 'string') return v;
  }
  return '';
}

/**
 * Extract the first name (or full name if no space) from a display name
 * for the welcome greeting. Falls back to the raw value if it looks like
 * an email address.
 */
function getFirstName(displayName: string): string {
  if (!displayName) return '';
  // If it contains '@' it is an email — show it as-is
  if (displayName.includes('@')) return displayName;
  return displayName.split(' ')[0] ?? displayName;
}

// ---------------------------------------------------------------------------
// Active link underline color per role
// ---------------------------------------------------------------------------

function getActiveLinkColor(role: UserRole | undefined): string {
  return role === 'org_admin' ? COLOR.secondary.main : COLOR.primary.main;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AppShellProps {
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Sub-component: desktop nav link with active underline
// ---------------------------------------------------------------------------

interface DesktopNavLinkProps {
  link: NavLink;
  isActive: boolean;
  activeColor: string;
}

function DesktopNavLink({ link, isActive, activeColor }: DesktopNavLinkProps): JSX.Element {
  return (
    <Link
      component={RouterLink}
      to={link.href}
      underline="none"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 1,
        fontSize: '0.9375rem',      // 15px — clean, readable
        fontWeight: isActive ? 600 : 500,
        color: isActive ? COLOR.text.primary : COLOR.text.secondary,
        cursor: 'pointer',
        transition: 'color 0.15s ease',
        // Active underline bar
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: isActive ? '80%' : '0%',
          height: '2.5px',
          borderRadius: '2px',
          backgroundColor: activeColor,
          transition: 'width 0.2s ease',
        },
        '&:hover': {
          color: COLOR.text.primary,
          '&::after': {
            width: '80%',
            backgroundColor: activeColor,
          },
        },
      }}
    >
      {link.badge ? (
        <Badge
          badgeContent={0}
          color="success"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16,
              backgroundColor: COLOR.primary.main,
              color: COLOR.primary.contrastText,
            },
          }}
        >
          {link.label}
        </Badge>
      ) : (
        link.label
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppShell({ children }: AppShellProps): JSX.Element {
  const { user } = useAuthStore();
  const location = useLocation();

  const isAuthenticated = user !== null;
  const role = getUserRole(user);
  const navLinks = isAuthenticated ? getNavLinks(role) : PUBLIC_LINKS;
  const displayName = getUserDisplayName(user);
  const photoURL = getUserPhotoUrl(user);
  const firstName = getFirstName(displayName);
  const activeColor = getActiveLinkColor(role);

  // ---- Drawer state -------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => { setDrawerOpen(true); }, []);
  const closeDrawer = useCallback(() => { setDrawerOpen(false); }, []);

  // Close the mobile nav drawer on EVERY navigation. Without this, navigating
  // (or a nav-link click that didn't fire closeDrawer) left the drawer +
  // its full-screen backdrop mounted, greying out the destination page and
  // swallowing every pointer event — the recurring "dead / greyed-out page".
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // ---- Global search state (Cmd/Ctrl+K or the AppBar search button) -------
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => { setSearchOpen(true); }, []);
  const closeSearch = useCallback(() => { setSearchOpen(false); }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, []);

  // ---- User menu state ----------------------------------------------------
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleSignOut = useCallback(async () => {
    closeMenu();
    await authService.signOut();
  }, [closeMenu]);

  // ---- Drawer content -----------------------------------------------------
  const drawerContent = (
    <Box
      sx={{ width: DRAWER_WIDTH, pt: 2 }}
      role="presentation"
    >
      {/* Drawer header */}
      <Box
        sx={{
          px: 2,
          pb: 2,
          mb: 1,
        }}
      >
        <Logo size="medium" />
        {isAuthenticated && firstName && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: COLOR.text.secondary,
              textTransform: 'uppercase',
            }}
          >
            Welcome, {firstName}!
          </Typography>
        )}
      </Box>
      <Divider />
      <List>
        {navLinks.map((link) => (
          <ListItem key={link.href} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={link.href}
              onClick={closeDrawer}
              selected={location.pathname === link.href}
              sx={{
                '&.Mui-selected': {
                  borderLeft: `3px solid ${activeColor}`,
                  backgroundColor: role === 'org_admin'
                    ? COLOR.secondary.lighter
                    : COLOR.primary.lighter,
                },
              }}
            >
              <ListItemText primary={link.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {!isAuthenticated && (
          <>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/login" onClick={closeDrawer}>
                <ListItemText primary="Sign In" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/register" onClick={closeDrawer}>
                <ListItemText primary="Sign Up" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* ------------------------------------------------------------------ */}
      {/* AppBar                                                               */}
      {/* ------------------------------------------------------------------ */}
      <AppBar
        position="sticky"
        component="header"
        elevation={0}
        sx={{
          backgroundColor: COLOR.background.default,
          borderBottom: `1px solid ${COLOR.divider}`,
          // No gradient bar — clean Figma spec
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          {/* Hamburger — always in DOM, visible on mobile via sx display */}
          <IconButton
            aria-label="Open navigation menu"
            edge="start"
            onClick={openDrawer}
            sx={{ mr: 1, display: { md: 'none' } }}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>

          {/* Branding — Logo wordmark */}
          <Link
            component={RouterLink}
            to="/"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'inherit',
              mr: { xs: 1, md: 2 },
              flexShrink: 0,
            }}
          >
            <Logo size="small" />
          </Link>

          {/* Welcome greeting — authenticated only, desktop */}
          {isAuthenticated && firstName && (
            <Typography
              component="span"
              sx={{
                display: { xs: 'none', md: 'block' },
                fontWeight: 700,
                fontSize: '0.8125rem',  // 13px — compact but legible
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: COLOR.text.primary,
                mr: 3,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Welcome, {firstName}!
            </Typography>
          )}

          {/* Desktop nav links — right-aligned per Figma */}
          <Box
            component="nav"
            aria-label="Main navigation"
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0,
              flexGrow: 1,
            }}
          >
            {navLinks.map((link) => (
              <DesktopNavLink
                key={link.href}
                link={link}
                isActive={location.pathname === link.href}
                activeColor={activeColor}
              />
            ))}
          </Box>

          {/* Spacer for mobile so right-side controls are pushed right */}
          <Box sx={{ flexGrow: 1, display: { md: 'none' } }} />

          {/* Global search — available to every role, results role-scoped */}
          <IconButton
            aria-label="Search (Cmd+K)"
            onClick={openSearch}
            color="inherit"
            sx={{ mr: 0.5 }}
          >
            <SearchIcon />
          </IconButton>

          {/* Right-side controls */}
          {isAuthenticated ? (
            <>
              {/* User account menu button — shows the avatar when set */}
              <IconButton
                aria-label="Open account menu"
                aria-controls="profile-menu"
                aria-haspopup="true"
                aria-expanded={menuAnchor !== null ? 'true' : undefined}
                onClick={openMenu}
                color="inherit"
                sx={{ p: 0.5 }}
              >
                {photoURL !== '' ? (
                  <Avatar
                    src={photoURL}
                    alt={displayName}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>

              {/* Rich account drill-in (identity + avatar upload + links) */}
              <ProfileMenu
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={closeMenu}
                onSignOut={() => { void handleSignOut(); }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to="/login"
                color="inherit"
                variant="text"
                sx={{ color: COLOR.text.secondary }}
              >
                Sign In
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                color="primary"
                sx={{ borderRadius: 6 }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* ------------------------------------------------------------------ */}
      {/* Mobile navigation drawer                                            */}
      {/* ------------------------------------------------------------------ */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        aria-label="Navigation menu"
      >
        {drawerContent}
      </Drawer>

      {/* Site-wide role-scoped search dialog */}
      <GlobalSearch open={searchOpen} onClose={closeSearch} role={role} />

      {/* ------------------------------------------------------------------ */}
      {/* Main content area — warm peach background from Figma               */}
      {/* ------------------------------------------------------------------ */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: CONTENT_BG,
        }}
      >
        {/* When used as a layout route (no children), render Outlet */}
        {children ?? <Outlet />}
      </Box>
    </Box>
  );
}
